import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Users, Receipt, TrendingUp, ShieldCheck, LogOut, Moon, Sun, Search, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import { api, formatIDR, formatDate, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useTheme } from "../context/ThemeContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { SkeletonCard } from "../components/Skeleton";
import OwnerProfitPanel from "../components/OwnerProfitPanel";

export default function AdminPage() {
    const navigate = useNavigate();
    const { logout, user } = useAuth();
    const { theme, toggle } = useTheme();
    const [stats, setStats] = useState(null);
    const [warungs, setWarungs] = useState(null);
    const [txs, setTxs] = useState(null);
    const [search, setSearch] = useState("");
    const [refreshing, setRefreshing] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState("");
    const [profits, setProfits] = useState(null);
    const [profitsLoading, setProfitsLoading] = useState(true);
    const [profitsError, setProfitsError] = useState("");

    useEffect(() => {
        load();
    }, []);

    const load = async () => {
        setError("");
        const profitsRequest = loadProfits();
        try {
            const [s, w, t] = await Promise.all([
                api.get("/admin/dashboard"),
                api.get("/admin/users?role=mitra&take=50"),
                api.get("/admin/transactions?take=50"),
            ]);
            setStats(s.data);
            setWarungs((Array.isArray(w.data) ? w.data : w.data.items ?? []).map((item) => ({ ...item, owner_name: item.fullName, active: item.status === "active", created_at: item.createdAt })));
            setTxs(Array.isArray(t.data) ? t.data : t.data.items ?? []);
        } catch (err) { setError(formatApiError(err)); setWarungs([]); setTxs([]); }
        await profitsRequest;
    };

    const loadProfits = async () => {
        setProfitsLoading(true);
        setProfitsError("");
        try {
            const { data } = await api.get("/admin/profits");
            setProfits(data);
        } catch (err) {
            setProfitsError(formatApiError(err));
        } finally {
            setProfitsLoading(false);
        }
    };

    const filteredWarungs = useMemo(() => {
        if (!warungs) return [];
        const q = search.trim().toLowerCase();
        if (!q) return warungs;
        return warungs.filter((item) =>
            [item.warung_name, item.owner_name, item.email, item.phone]
                .some((value) => value?.toString().toLowerCase().includes(q)),
        );
    }, [warungs, search]);

    const refresh = async () => {
        setRefreshing(true);
        await load();
        setRefreshing(false);
    };

    const toggleActive = async (item) => {
        if (item.active == null || !window.confirm(`${item.active ? "Nonaktifkan" : "Aktifkan"} akun ${item.email}?`)) return;
        setSaving(true);
        try {
            await api.patch(`/admin/users/${item.id}/${item.active ? "suspend" : "activate"}`);
            toast.success(`Status ${item.owner_name} berhasil diperbarui.`);
            await load();
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="min-h-screen bg-background">
            <header className="sticky top-0 z-40 bg-background/85 backdrop-blur-xl border-b border-border">
                <div className="max-w-6xl mx-auto px-5 py-3 flex items-center justify-between">
                    <div className="flex items-center gap-2.5">
                        <div className="h-9 w-9 rounded-xl bg-brand-deep dark:bg-brand-green flex items-center justify-center">
                            <ShieldCheck className="h-5 w-5 text-white dark:text-brand-deeper" />
                            <div className="h-12 w-12 flex-shrink-0 flex items-center justify-center">
                                <img
                                    src="/images/LG_P4YU.png"
                                    alt="WarungPay Logo"
                                    className="w-full h-full object-contain"
                                />
                            </div>
                        </div>
                        <div>
                            <div className="font-heading font-extrabold text-base leading-none">WARUNGPAY</div>
                            <div className="text-[10px] uppercase tracking-widest text-brand-green font-semibold mt-0.5">Admin Panel</div>
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={toggle} className="rounded-full" data-testid="admin-theme-toggle">
                            {theme === "dark" ? <Sun className="h-5 w-5" /> : <Moon className="h-5 w-5" />}
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => navigate("/admin/users")} data-testid="admin-manage-users">
                            Kelola User
                        </Button>
                        <Button variant="outline" size="sm" onClick={() => navigate("/admin/provider/digiflazz")}>Provider / Digiflazz</Button>
                        <Button variant="outline" size="sm" onClick={() => navigate("/admin/operasional")}>Operasional</Button>
                        <Button variant="outline" size="sm" onClick={() => navigate("/dashboard")} data-testid="admin-back-mitra">
                            Mode Mitra
                        </Button>
                        <Button variant="ghost" size="icon" onClick={async () => { await logout(); navigate("/login/rahasia-admin"); }} className="rounded-full" data-testid="admin-logout-btn">
                            <LogOut className="h-5 w-5" />
                        </Button>
                    </div>
                </div>
            </header>

            <main className="max-w-6xl mx-auto px-5 py-6 space-y-5">
                <div>
                    <h1 className="font-heading font-extrabold text-3xl tracking-tight">Dashboard Admin</h1>
                    <p className="text-muted-foreground text-sm mt-1">Selamat datang, {user?.owner_name}.</p>
                </div>

                {error && <div role="alert" className="rounded-xl border border-destructive p-4 text-sm text-destructive">{error}<Button variant="outline" className="ml-3" onClick={refresh} disabled={refreshing}>Coba lagi</Button></div>}
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                    {!stats && !error ? (<><SkeletonCard /><SkeletonCard /><SkeletonCard /><SkeletonCard /></>) : stats ? (
                        <>
                            <BigStat icon={<Users />} label="Total Pengguna" value={stats.users?.total ?? 0} />
                            <BigStat icon={<Receipt />} label="Total Transaksi" value={stats.transactions?.total ?? 0} />
                            <BigStat icon={<TrendingUp />} label="Pengguna Aktif" value={stats.users?.active ?? 0} green />
                            <BigStat icon={<TrendingUp />} label="Callback 24 Jam" value={stats.callbacks?.last24h ?? 0} green />
                        </>
                    ) : null}
                </div>

                <OwnerProfitPanel data={profits} loading={profitsLoading} error={profitsError} onRefresh={loadProfits} />

                <Tabs defaultValue="warungs" className="w-full">
                    <TabsList className="grid grid-cols-2 max-w-md">
                        <TabsTrigger value="warungs" data-testid="tab-warungs">Manajemen User</TabsTrigger>
                        <TabsTrigger value="txs" data-testid="tab-txs">Transaksi</TabsTrigger>
                    </TabsList>

                    <TabsContent value="warungs" className="mt-4">
                        <div className="rounded-2xl bg-card border border-border p-4 space-y-4">
                            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                                <div>
                                    <div className="text-sm font-semibold">Kelola Mitra Warung</div>
                                    <div className="text-xs text-muted-foreground">50 mitra terbaru. Buka Kelola User untuk pencarian lengkap.</div>
                                </div>
                                <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
                                    <div className="relative w-full sm:w-80">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                                        <Input
                                            aria-label="Cari mitra"
                                            value={search}
                                            onChange={(e) => setSearch(e.target.value)}
                                            placeholder="Cari nama, email, atau nomor"
                                            className="h-11 rounded-xl pl-10"
                                        />
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={refresh}
                                        disabled={refreshing}
                                        className="rounded-xl"
                                    >
                                        {refreshing ? "Menyegarkan..." : (<><RotateCcw className="h-4 w-4 mr-2" />Segarkan</>)}
                                    </Button>
                                </div>
                            </div>

                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                                        <tr>
                                            <th className="text-left p-3 font-semibold">ID User</th>
                                            <th className="text-left p-3 font-semibold">Pemilik</th>
                                            <th className="text-left p-3 font-semibold">Email</th>
                                            <th className="text-left p-3 font-semibold">Status</th>
                                            <th className="text-left p-3 font-semibold">Daftar</th>
                                            <th className="text-center p-3 font-semibold">Aksi</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {!warungs ? (
                                            Array.from({ length: 4 }).map((_, i) => (
                                                <tr key={i}><td colSpan={8} className="p-3"><div className="h-8 bg-muted animate-pulse rounded" /></td></tr>
                                            ))
                                        ) : filteredWarungs.length === 0 ? (
                                            <tr><td colSpan={8} className="p-8 text-center text-muted-foreground">Tidak ada mitra dengan kata kunci tersebut.</td></tr>
                                        ) : (
                                            filteredWarungs.map((w) => (
                                                <tr key={w.id} className="border-t border-border" data-testid={`warung-row-${w.id}`}>
                                                    <td className="p-3 text-xs" title={w.id}>{w.id.slice(0, 8)}</td>
                                                    <td className="p-3">{w.owner_name}</td>
                                                    <td className="p-3 text-muted-foreground">{w.email}</td>
                                                    <td className="p-3">
                                                        {w.active == null ? (
                                                            <span className="text-muted-foreground">-</span>
                                                        ) : (
                                                            <span className={`inline-flex rounded-full px-2 py-1 text-[10px] font-semibold ${w.active ? "bg-emerald-100 text-emerald-700" : "bg-rose-100 text-rose-700"}`}>
                                                                {w.active ? "Aktif" : "Nonaktif"}
                                                            </span>
                                                        )}
                                                    </td>
                                                    <td className="p-3 text-xs text-muted-foreground">{formatDate(w.created_at)}</td>
                                                    <td className="p-3 text-center">
                                                        {w.active == null ? (
                                                            <span className="text-xs text-muted-foreground">Tidak dapat diubah</span>
                                                        ) : (
                                                            <Button
                                                                variant="outline"
                                                                size="sm"
                                                                onClick={() => toggleActive(w)}
                                                                disabled={saving}
                                                            >
                                                                {w.active ? "Nonaktifkan" : "Aktifkan"}
                                                            </Button>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </TabsContent>

                    <TabsContent value="txs" className="mt-4">
                        <p className="text-sm text-muted-foreground mb-3">50 transaksi terbaru. <button className="underline" onClick={() => navigate("/admin/operasional")}>Buka operasional untuk seluruh transaksi dan filter status.</button></p>
                        <div className="rounded-2xl bg-card border border-border overflow-hidden">
                            <div className="overflow-x-auto">
                                <table className="w-full text-sm">
                                    <thead className="bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">
                                        <tr>
                                            <th className="text-left p-3 font-semibold">Waktu</th>
                                            <th className="text-left p-3 font-semibold">Produk</th>
                                            <th className="text-left p-3 font-semibold">Pelanggan</th>
                                            <th className="text-right p-3 font-semibold">Total</th>
                                            <th className="text-center p-3 font-semibold">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {!txs ? (
                                            Array.from({ length: 4 }).map((_, i) => (
                                                <tr key={i}><td colSpan={5} className="p-3"><div className="h-8 bg-muted animate-pulse rounded" /></td></tr>
                                            ))
                                        ) : txs.length === 0 ? (
                                            <tr><td colSpan={5} className="p-8 text-center text-muted-foreground">Belum ada transaksi</td></tr>
                                        ) : (
                                            txs.map((t) => (
                                                <tr key={t.id} className="border-t border-border" data-testid={`admin-tx-row-${t.id}`}>
                                                    <td className="p-3 text-xs text-muted-foreground">{formatDate(t.createdAt || t.created_at)}</td>
                                                    <td className="p-3 font-semibold">{t.product?.name || t.product_name}</td>
                                                    <td className="p-3">{t.targetNumber || t.customer_number}</td>
                                                    <td className="p-3 text-right font-heading font-bold">{formatIDR(t.totalAmount ?? t.total ?? t.sellPrice)}</td>
                                                    <td className="p-3 text-center">
                                                        <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase ${t.status === "SUCCESS" ? "bg-brand-green/10 text-brand-green" : ["PENDING", "PROCESSING"].includes(t.status) ? "bg-amber-500/10 text-amber-700" : "bg-rose-500/10 text-rose-500"}`}>
                                                            {t.status}
                                                        </span>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </TabsContent>
                </Tabs>
            </main>
        </div>
    );
}

function BigStat({ icon, label, value, green }) {
    return (
        <motion.div initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl bg-card border border-border p-5">
            <div className={`h-10 w-10 rounded-xl flex items-center justify-center ${green ? "bg-brand-green/10 text-brand-green" : "bg-brand-deep/10 text-brand-deep dark:bg-brand-green/10 dark:text-brand-green"}`}>
                {icon}
            </div>
            <div className="text-xs text-muted-foreground mt-3">{label}</div>
            <div className={`font-heading font-bold text-2xl mt-1 ${green ? "text-brand-green" : ""}`}>{value}</div>
        </motion.div>
    );
}
